import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import api from '../utils/api';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

const REGINA_CENTER = [-104.6189, 50.4501];
const REGINA_ZOOM = 11.5;

const DOT_LAYERS = [
  { key: 'checkins',        color: '#94a3b8', radius: 5 },
  { key: 'issues_open',     color: '#ef4444', radius: 6 },
  { key: 'issues_verified', color: '#fb923c', radius: 7 },
  { key: 'issues_resolved', color: '#4ade80', radius: 6 },
  { key: 'badges',          color: '#fbbf24', radius: 7 },
];

export default function HeroMap({ visible }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Bail out if token missing, already initialized, or WebGL not supported
    if (!mapboxgl.accessToken || map.current || !mapContainer.current) return;
    if (!mapboxgl.supported()) return;

    try {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: REGINA_CENTER,
        zoom: REGINA_ZOOM,
        interactive: false,
        attributionControl: false,
        failIfMajorPerformanceCaveat: false,
      });

      map.current.on('load', async () => {
        try {
          const res = await api.get('/map-snapshot');
          const data = res.data;

          DOT_LAYERS.forEach(({ key, color, radius }) => {
            const coords = (data[key] || []).map(([lat, lng]) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: [lng, lat] }
            }));
            if (!coords.length) return;

            map.current.addSource(`src-${key}`, {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: coords }
            });

            // Glow halo
            map.current.addLayer({
              id: `layer-${key}-glow`,
              type: 'circle',
              source: `src-${key}`,
              paint: {
                'circle-radius': radius + 5,
                'circle-color': color,
                'circle-opacity': 0.2,
                'circle-blur': 1,
              }
            });

            // Main dot
            map.current.addLayer({
              id: `layer-${key}`,
              type: 'circle',
              source: `src-${key}`,
              paint: {
                'circle-radius': radius,
                'circle-color': color,
                'circle-opacity': 0.9,
                'circle-stroke-width': 1.5,
                'circle-stroke-color': color,
              }
            });
          });
        } catch (e) {
          console.warn('Map snapshot failed:', e);
        }
        setReady(true);
      });

      map.current.on('error', (e) => {
        console.warn('Mapbox error (non-fatal):', e);
      });

    } catch (e) {
      console.warn('Mapbox init failed (WebGL unavailable):', e);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []); // eslint-disable-line

  return (
    <div
      ref={mapContainer}
      className="absolute inset-0 w-full h-full"
      style={{
        opacity: visible && ready ? 1 : 0,
        transition: 'opacity 1.8s ease-in-out',
        pointerEvents: 'none',
      }}
    />
  );
}
